import * as ng from 'angular';
import * as _ from 'underscore';

import { FormFieldsController } from './form-fields.component';
import { FormField } from '../../shared/models/self-reg-settings';

const fieldViewsByName = {
   email: require('./form-field-email.html'),
   confirmEmail: require('./form-field-confirm-email.html'),
   country: require('./form-field-country.html'),
   dob: require('./form-field-dob.html'),
   phoneHome: require('./form-field-phone.html'),
   sex: require('./form-field-gender.html'),
   state: require('./form-field-state.html'),
   termsAgree: require('./form-field-terms-agree.html'),
   zip: require('./form-field-postal.html'),
   ageCategory: require('./form-field-age-category.html'),
   addressSearch: require('./form-field-location-lookup.html')
};

const fieldViewsByType = {
   checkbox: require('./form-field-checkbox.html'),
   surveyBoolean: require('./form-survey-boolean.html'),
   surveyOption: require('./form-survey-option.html'),
   surveyMultioption: require('./form-survey-multioption.html'),
   surveyText: require('./form-survey-text.html'),
   text: require('./form-field-text.html'),
   textLabel: require('./form-field-richtext.html'),
   optin: require('./form-field-optin.html')
};

export class FormFieldController implements ng.IComponentController {
   static $inject = ['$sce'];

   constructor(private $sce:ng.ISCEService) {
      this.uid = _.uniqueId();
   }

   formCtrl:FormFieldsController;

   get form() {
      return this.formCtrl.ngFormCtrl;
   }

   field:FormField;
   readonly uid:string;

   $onInit() {
      // editors are using field key/name relative to pre-registration records
      // sadly there is some inconsistencies with naming on participant records
      // this is checking for those fields and adjusting key name
      if (this.field.key === 'phoneNumber') {
         this.field.key = 'phoneHome';
      }

      if (this.field.key === 'dateOfBirth') {
         this.field.key = 'dob';
      }

      if (this.field.key === 'gender') {
         this.field.key = 'sex';
      }

      // editors create a label on checkboxes and opt-ins despite not having any use
      if (this.field.type === 'checkbox' || this.field.type == 'optin') {
         this.field.label = null;
      }

      if (this.field.key === 'country') {
         this.onCountryChange();
      }

      // editors are setting bonkers values for "checked by default" option
      // on checkboxes ("0", "True", "False")
      if (this.field.value && this.field.value.toLowerCase() === 'true') {
         if (this.field.type === 'checkbox' || this.field.type === 'optin') {
            this.model[this.field.key] = true;
         }

         if (this.field.type === 'surveyBoolean') {
            this.surveyValue = 'true';
         }
      }
   }

   onCountryChange() {
      this.model[this.field.key] = this.formCtrl.country.code;
   }

   onPickPlace(place) {
      this.model.address = place.address;

      if (this.formCtrl.ngFormCtrl['country']) {
         this.formCtrl.country = _.findWhere(this.formCtrl.countries, {code: place.countryCode});
      }

      if (this.formCtrl.ngFormCtrl['state']) {
         this.model.state = place.stateCode;
      }

      if (this.formCtrl.ngFormCtrl['postalCode']) {
         this.model.zip = place.postalCode;
      }

      if (this.formCtrl.ngFormCtrl['city']) {
         this.model.city = place.city;
      }
   }

   get height() {
      return this.field.type == 'textLabel' ? `${this.field.sizeY * 11}px` : `${this.field.sizeY * 22}px`;
   }

   get model() {
      return this.formCtrl.model;
   }

   get language() {
      return this.formCtrl.language;
   }

   get isRequired() {
      if (this.field.key === 'zip') {
         // "required" always false when country doesn't use "postal code"
         if (this.formCtrl.country && !this.formCtrl.country.postalRegex) {
            return false;
         } else {
            return this.field.required;
         }
      }
      return this.field.required;
   }

   get label() {
      if (this.field.label && this.field.label[this.language]) {
         return this.field.label[this.language];
      }

      if (_.contains(['surveyOption', 'surveyMultioption', 'surveyText'], this.field.type)) {
         return this.field.survey.description[this.language];
      }
   }

   get placeholder() {
      if (this.field.placeholder && this.field.placeholder[this.language]) {
         return this.field.placeholder[this.language];
      }
   }

   get infoUrl() {
      if (this.field.infoURL && this.field.infoURL[this.language]) {
         return this.field.infoURL[this.language];
      }
   }

   get description() {
      if (this.field.description && this.field.description[this.language]) {
         return this.field.description[this.language];
      }
   }

   get descriptionHtml() {
      if (this.description) {
         return this.$sce.trustAsHtml(this.description);
      }
   }

   get templateUrl() {
      if (fieldViewsByName[this.field.key]) {
         return fieldViewsByName[this.field.key];
      }

      if (fieldViewsByType[this.field.type]) {
         return fieldViewsByType[this.field.type];
      }
   }

   _surveyValue:string;
   get surveyValue() {
      return this._surveyValue;
   }

   set surveyValue(val:string) {
      this._surveyValue = val;

      if (!this.model.surveyItems) {
         this.model.surveyItems = [];
      }

      let surveyItem = _.find(this.model.surveyItems, i => i.name === this.field.survey.key);

      if (!val) {
         this.model.surveyItems = _.without(this.model.surveyItems, surveyItem);
      } else {
         if (!surveyItem) {
            surveyItem = { name: this.field.survey.key, value: val };
            this.model.surveyItems.push(surveyItem);
         } else {
            surveyItem.value = val;
         }
      }
   }

   get surveyChoices() {
      return _.chain(this.field.survey.choices)
         .filter(c => !_.contains(this.field.hiddenChoices, c.key))
         .sortBy('index')
         .value();
   }

   hasChoice(choiceKey:string):boolean {
      if (this.surveyValue) {
         return _.contains(this.surveyValue.split(','), choiceKey);
      }

      return false;
   }

   toggleChoice(e:MouseEvent, choiceKey:string) {
      let values = this.surveyValue ? this.surveyValue.split(',') : [];

      if (_.contains(values, choiceKey)) {
         values = _.without(values, choiceKey);
      } else {
         if (this.field.maxChoices && values.length >= this.field.maxChoices) {
            e.preventDefault();
            return;
         }

         values.push(choiceKey);
      }

      this.surveyValue = values.join(',');
   }
}

ng.module('selfReg').component('formField', {
   templateUrl: require('./form-field.html'),
   controller: FormFieldController,
   require: {
      formCtrl: '^form-fields'
   },
   bindings: {
      field: '<'
   }
});
